import { isSetupPath } from '#shared/onboarding/flow'
import { isSimulatedCheckoutPath } from '#shared/billing/simulatedCheckout'
import { mfaRedirectTarget } from '#shared/organizations/userProfile'
import { isFreeTierProfile } from '#shared/organizations/freeTrial'
import { LANDING_VERIFY_EMAIL_PATH } from '#shared/landingResearch'
import { isMarketingPath } from '~/utils/marketingRoutes'

export default defineNuxtRouteMiddleware(async (to) => {
  if (to.path === '/addin') return
  if (to.path === '/mcp' || to.path.startsWith('/.well-known/') || to.path.startsWith('/oauth/')) return
  // Public marketing routes (/, /research, /privacidad, /terminos, /blog/**)
  // never redirect on auth state and initialize Firebase in the background on the client.
  if (isMarketingPath(to.path)) return

  const { user, waitForAuth, isEmailUnverified } = useAuth()
  const { $officeReady, $officeToken, $officeLoading } = useNuxtApp()

  // Wait for Firebase Auth initialization and the first onIdTokenChanged
  // settlement before evaluating route redirects (no-ops during SSR).
  await waitForAuth()

  // While Office.js is still loading in iframe, don't redirect
  if (($officeLoading as Ref<boolean>)?.value) return

  // Inside Word: authenticated via Office dialog token
  if ($officeReady?.value && $officeToken?.value) return

  // Inside Word but not yet authenticated: let the page show the login button
  if ($officeReady?.value && !$officeToken?.value) return

  const normalizedPath = to.path.length > 1 ? to.path.replace(/\/$/, '') : to.path
  const isLoginPath = normalizedPath === '/login'

  // Redirect logged-in users away from login page
  if (user.value && isLoginPath) {
    if (typeof to.query.invite === 'string') {
      return navigateTo(`/app/setup?invite=${encodeURIComponent(to.query.invite)}`)
    }
    const redirect = typeof to.query.redirect === 'string' ? to.query.redirect : ''
    if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) {
      return navigateTo(redirect)
    }
    if (!import.meta.server) {
      const { profile, ensure } = useUserProfile()
      await ensure?.()
      // A free-tier user — no organization, or a member of their own free-trial organization (MAR-1452) — lands
      // on the free chat, as `/login` sends them — unless their email is unverified: `/chat` would bounce them to
      // the verify page, the loop shape #1690 removed from `login.vue`, so they go there directly (MAR-1465).
      if (profile?.value && 'organization_name' in profile.value && isFreeTierProfile(profile.value)) {
        return navigateTo(isEmailUnverified() ? LANDING_VERIFY_EMAIL_PATH : '/chat')
      }
    }
    return navigateTo('/app')
  }

  // Redirect unauthenticated users to login (or /#investigar for /chat, MAR-1217)
  if (!user.value && !isLoginPath) {
    // A returning user whose one-hour `__session` lapsed: SSR renders the app's loading shell and the
    // client, which can restore the Firebase session, makes this call instead (MAR-1421, `auth-ssr.ts`).
    if (import.meta.server && useState<boolean>('auth-deferred', () => false).value) return
    if (normalizedPath === '/chat') {
      return navigateTo('/#investigar')
    }
    return navigateTo(`/login?redirect=${encodeURIComponent(to.fullPath)}`)
  }

  // The simulated checkout page (MAR-1130) is where the plan step itself sends a founder to pay: the
  // onboarding gate below would send them straight back to /app/setup. It still needs the session
  // above, since billing answers only an admin of the organization.
  if (user.value && isSimulatedCheckoutPath(to.path)) return

  // Free-tier /chat workspace (MAR-1217) serves organization-less users; its own page guard
  // routes paid org members to /app from the loaded profile (not the App Check-gated GET /api/landing-research,
  // MAR-1240), and queries that endpoint for everyone else, sending unverified users to /#investigar.
  if (user.value && normalizedPath === '/chat') return

  // Onboarding gate fetches /api/profile — keep it client-side (after Firebase
  // hydrates) so SSR doesn't make an authed fetch. The server has already
  // rendered the authed shell above; the client reruns this check on hydration.
  if (import.meta.server) return

  // For authenticated users navigating to app pages: gate on organization mandatory 2FA (`MAR-1181`)
  // and onboarding (`MAR-933`).
  if (user.value && !isLoginPath) {
    const { profile, ensure } = useUserProfile()
    await ensure?.()
    const mfaRedirect = mfaRedirectTarget(to.path, profile.value)
    if (mfaRedirect) {
      return navigateTo(mfaRedirect)
    }
    if (normalizedPath === '/app/settings/security' && profile.value?.organization_require_mfa && !profile.value?.mfa_enrolled) {
      return
    }
    const { nextStep, leaveIfMidFlow } = useOnboarding()
    const step = await nextStep()
    // `isSetupPath` ignores a trailing slash: `/app/setup/` is the same page, and must never count as leaving it.
    const onSetup = isSetupPath(to.path)
    // O4: a user who entered the optional phase and is now anywhere else has LEFT it — what is still
    // unanswered is stored as skipped, once (a reload of /app/setup itself is not leaving). Not
    // awaited: it changes no route, and the reminders read `offered` and `skipped` alike meanwhile.
    if (!step && !onSetup) leaveIfMidFlow()
    const hasInvite = typeof to.query.invite === 'string'
    if (!step && onSetup) {
      if (hasInvite) return
      // Mid-flow: the optional phase was entered (so the gate no longer holds the user here) but the
      // Done screen was not reached. A reload, or coming back on purpose, resumes the remaining steps.
      // Nothing ever SENDS a user here in this state — leaving counts as skipping.
      const onboarding = profile.value?.onboarding
      if (onboarding?.started && !onboarding.finished && !profile.value?.org_override_id) return
      return navigateTo('/app')
    }
    if (!step) return
    if (onSetup) return
    return navigateTo('/app/setup')
  }
})
